/**
 * Utility functions for collecting browser and device information
 * for form submissions
 */

export interface BrowserInfo {
  userAgent: string;
  browser: string;
  browserVersion: string;
  os: string;
  device: string;
  language: string;
  screenResolution: string;
  timezone: string;
  cookiesEnabled: boolean;
  onlineStatus: boolean;
  referrer: string;
  timestamp: string;
}

/**
 * Parse user agent string to extract browser information
 */
function parseBrowser(userAgent: string): { browser: string; browserVersion: string } {
  const ua = userAgent.toLowerCase();
  
  if (ua.includes('firefox')) {
    const match = userAgent.match(/firefox\/(\d+\.\d+)/i);
    return { browser: 'Firefox', browserVersion: match ? match[1] : 'Unknown' };
  }
  
  if (ua.includes('chrome') && !ua.includes('edg')) {
    const match = userAgent.match(/chrome\/(\d+\.\d+)/i);
    return { browser: 'Chrome', browserVersion: match ? match[1] : 'Unknown' };
  }
  
  if (ua.includes('safari') && !ua.includes('chrome')) {
    const match = userAgent.match(/version\/(\d+\.\d+)/i);
    return { browser: 'Safari', browserVersion: match ? match[1] : 'Unknown' };
  }
  
  if (ua.includes('edg')) {
    const match = userAgent.match(/edg\/(\d+\.\d+)/i);
    return { browser: 'Edge', browserVersion: match ? match[1] : 'Unknown' };
  }
  
  if (ua.includes('opera') || ua.includes('opr')) {
    const match = userAgent.match(/(opera|opr)\/(\d+\.\d+)/i);
    return { browser: 'Opera', browserVersion: match ? match[2] : 'Unknown' };
  }
  
  return { browser: 'Unknown', browserVersion: 'Unknown' };
}

/**
 * Parse user agent string to extract OS information
 */
function parseOS(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  
  if (ua.includes('windows nt 10')) return 'Windows 10/11';
  if (ua.includes('windows nt 6.3')) return 'Windows 8.1';
  if (ua.includes('windows nt 6.2')) return 'Windows 8';
  if (ua.includes('windows nt 6.1')) return 'Windows 7';
  if (ua.includes('windows')) return 'Windows';
  
  if (ua.includes('mac os x')) {
    const match = userAgent.match(/mac os x (\d+[._]\d+)/i);
    if (match) {
      const version = match[1].replace('_', '.');
      return `macOS ${version}`;
    }
    return 'macOS';
  }
  
  if (ua.includes('iphone')) return 'iOS (iPhone)';
  if (ua.includes('ipad')) return 'iOS (iPad)';
  if (ua.includes('android')) {
    const match = userAgent.match(/android (\d+\.\d+)/i);
    return match ? `Android ${match[1]}` : 'Android';
  }
  
  if (ua.includes('linux')) return 'Linux';
  if (ua.includes('ubuntu')) return 'Ubuntu';
  if (ua.includes('debian')) return 'Debian';
  if (ua.includes('fedora')) return 'Fedora';
  
  return 'Unknown';
}

/**
 * Determine device type based on user agent and screen size
 */
function getDeviceType(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  const screenWidth = window.screen?.width || 0;
  
  if (ua.includes('mobile') || ua.includes('iphone') || ua.includes('android')) {
    return 'Mobile';
  }
  
  if (ua.includes('ipad') || ua.includes('tablet')) {
    return 'Tablet';
  }
  
  if (screenWidth > 0) {
    if (screenWidth < 768) return 'Mobile';
    if (screenWidth < 1024) return 'Tablet';
    return 'Desktop';
  }
  
  return 'Desktop';
}

/**
 * Get user's timezone
 */
function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return 'Unknown';
  }
}

/**
 * Get current timestamp in ISO format
 */
function getTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Collect comprehensive browser and device information
 */
export function collectBrowserInfo(): BrowserInfo {
  const userAgent = navigator.userAgent || 'Unknown';
  const { browser, browserVersion } = parseBrowser(userAgent);
  const os = parseOS(userAgent);
  const device = getDeviceType(userAgent);
  
  return {
    userAgent,
    browser,
    browserVersion,
    os,
    device,
    language: navigator.language || navigator.languages?.[0] || 'Unknown',
    screenResolution: window.screen ? `${window.screen.width}x${window.screen.height}` : 'Unknown',
    timezone: getTimezone(),
    cookiesEnabled: navigator.cookieEnabled ?? false,
    onlineStatus: navigator.onLine ?? true,
    referrer: document.referrer || 'Direct',
    timestamp: getTimestamp(),
  };
}

/**
 * Format browser info for email or logging
 */
export function formatBrowserInfoForEmail(browserInfo: BrowserInfo): string {
  return `
--- Technical Information ---
User Agent: ${browserInfo.userAgent}
Browser: ${browserInfo.browser} ${browserInfo.browserVersion}
Operating System: ${browserInfo.os}
Device Type: ${browserInfo.device}
Screen Resolution: ${browserInfo.screenResolution}
Language: ${browserInfo.language}
Timezone: ${browserInfo.timezone}
Cookies Enabled: ${browserInfo.cookiesEnabled ? 'Yes' : 'No'}
Online Status: ${browserInfo.onlineStatus ? 'Online' : 'Offline'}
Referrer: ${browserInfo.referrer}
Timestamp: ${browserInfo.timestamp}
`.trim();
}